import {
  Box,
  CircularProgress,
  Collapse,
  makeStyles,
  TableBody,
  TableCell,
  TableRow,
  Theme,
  Typography,
  useTheme,
} from '@material-ui/core';
import { Row, Table } from '@tanstack/react-table';
import clsx from 'clsx';
import {
  CSSProperties,
  MouseEvent,
  ReactNode,
  memo,
  useCallback,
  useMemo,
} from 'react';
import { WbButton } from '../../WbButton';
import { WbExpandToggle, WbExpandToggleProps } from '../../WbExpandToggle';
import { useWbTableContext } from '../context/WbTableContext';
import { Selector } from '../Selector';
import { RenderRowDetail, TableCellProps, TableContentProps } from '../types';
import { explodeTreeRows, getColumnId } from '../utils/utils';
import { DefaultCell } from '../StandardCells/DefaultCell';
import { WbTableCell } from '../WbTableCell';
import { getCommonPinningStyles } from '../utils/columnPinningUtils';
import { useTableColumnPinning } from '../hooks/useTableState';
import { ACTIONS_COLUMN_ID } from '../StandardColumns';
import { DETAIL_EXPANDER_ID, SELECTOR_ID } from '../constants';

const useTreeCellStyles = makeStyles(theme => ({
  tableTreeCellRoot: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

const useRowStyles = makeStyles<Theme, { disableRowHighlightOnHover: boolean }>(
  theme => ({
    tableRow: props => ({
      position: 'relative',
      borderBottom: '1px solid white',
      backgroundColor: theme.palette.background.default,
      '&:hover': {
        backgroundColor: props.disableRowHighlightOnHover
          ? undefined
          : theme.palette.bkg.primary,
      },
    }),
    detailTableRowHidden: {
      '& > *': {
        border: 'none',
        padding: 0,
      },
    },
    detailTableCell: {
      padding: 0,
    },
    privateCell: {
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(1),
      userSelect: 'none',
      touchAction: 'none',
      overflow: 'hidden',
      textOverflow: 'clip',
    },
    sentinelCell: {
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(1),
    },
  }),
  { name: 'WbTableContent' },
);

interface TableTreeCellProps extends WbExpandToggleProps {
  children: ReactNode;
  depth: number;
  hasChildren: boolean;
}

function TableTreeCellWrapper({
  children,
  expanded,
  onClick,
  depth,
  hasChildren,
}: TableTreeCellProps) {
  const classes = useTreeCellStyles();
  const theme = useTheme();

  const paddingLeft = useMemo(() => theme.spacing(2) * depth, [theme, depth]);

  const style = useMemo<React.CSSProperties>(
    () => ({
      visibility: hasChildren ? 'visible' : 'hidden',
    }),
    [hasChildren],
  );

  return (
    <Box className={classes.tableTreeCellRoot} style={{ paddingLeft }}>
      <WbExpandToggle expanded={expanded} onClick={onClick} style={style} />
      {children}
    </Box>
  );
}

const MemoizedTableTreeCellWrapper = memo(TableTreeCellWrapper);

interface TableCellContentProps<T> {
  row: T;
  cell: TableCellProps<T>;
  rowDepth?: number;
}

export function TableCellContent<T>({
  row,
  cell,
  rowDepth = 0,
}: TableCellContentProps<T>) {
  if (cell.fieldRender) {
    return cell.fieldRender(row, { rowDepth });
  }
  const field = cell.field;

  if (!field) return null;

  const formattedValue = cell.formatter
    ? cell.formatter(row[field])
    : row[field];

  return <DefaultCell value={formattedValue} {...cell} />;
}

const MemoizedTableCellContent = memo(
  TableCellContent,
) as typeof TableCellContent;

interface TableRowComponentProps<T> {
  row: Row<T>;
  index: number;
  columns: TableCellProps<T>[];
  selection: 'single' | 'multiple' | undefined;
  selectedItems: T[] | undefined;
  setSelectedItems: ((items: T[]) => void) | undefined;
  tooltipIfDisabled: string | undefined;
  onRowClick?: any;
  rowDynamicClassName: ((row: T) => string | undefined) | undefined;
  treeProps: any;
  getRowId: ((row: T) => string) | undefined;
  checkIfDisabled: ((row: T) => boolean) | undefined;
  disableRowHighlightOnHover: boolean;
  theme: Theme;
  rowStyle: CSSProperties | undefined;
  rowClassName: string | undefined;
  depthMap: Map<string, number>;
  isAnyRowNested: boolean;
  tanstackTableInstance: Table<T>;
  renderRowDetail?: RenderRowDetail<T>;
}

function TableRowComponent<T>({
  row,
  index,
  columns,
  selection,
  selectedItems,
  setSelectedItems,
  tooltipIfDisabled,
  onRowClick,
  rowDynamicClassName,
  disableRowHighlightOnHover,
  treeProps,
  getRowId,
  checkIfDisabled,
  theme,
  rowStyle,
  rowClassName,
  depthMap,
  isAnyRowNested,
  tanstackTableInstance,
  renderRowDetail,
}: TableRowComponentProps<T>) {
  const id = getRowId?.(row.original);
  const classes = useRowStyles({ disableRowHighlightOnHover });

  const computedRowStyle = useMemo(() => {
    const baseStyle = {
      ...(onRowClick ? { cursor: 'pointer' } : {}),
      ...rowStyle,
    };

    const isSelected =
      selection === 'single' &&
      (selectedItems?.length ?? 0) > 0 &&
      getRowId?.(selectedItems![0]) === getRowId?.(row.original);

    const isMultiSelected =
      selection === 'multiple' &&
      selectedItems?.find(r => getRowId?.(r) === getRowId?.(row.original));

    if (isSelected || isMultiSelected) {
      return {
        ...baseStyle,
        backgroundColor: theme.palette.bkg.primary,
      };
    }

    return baseStyle;
  }, [
    onRowClick,
    rowStyle,
    selection,
    selectedItems,
    getRowId,
    row.original,
    theme.palette.bkg.primary,
  ]);

  const handleRowClick = useCallback(
    (event: MouseEvent) => {
      let targetElement: Element | null = event.target as Element;
      while (targetElement && targetElement instanceof Element) {
        if (targetElement.hasAttribute('block-row-click')) {
          return;
        }
        targetElement = targetElement.parentElement;
      }
      event.preventDefault();
      event.stopPropagation();
      if (onRowClick) onRowClick(row.original, event, index);
      if (selection === 'single') {
        if (checkIfDisabled?.(row.original)) return;
        setSelectedItems?.([row.original]);
      }
    },
    [
      onRowClick,
      row.original,
      index,
      selection,
      setSelectedItems,
      checkIfDisabled,
    ],
  );

  const detailExpanded = row.getIsExpanded?.();

  const handleExpandClick = useCallback(() => {
    if (!id || !treeProps) return;

    const expanded = treeProps.expandedRowsIds.includes(id);
    if (expanded) {
      treeProps.onExpandChange(
        treeProps.expandedRowsIds.filter((i: string) => i !== id),
      );
    } else {
      treeProps.onExpandChange([...treeProps.expandedRowsIds, id]);
      treeProps.onExpand?.(id); // trigger async child loading
    }
  }, [id, treeProps]);

  const rowDepth = id ? depthMap.get(id) ?? 0 : 0;

  // Use robust hook to track column pinning changes
  const columnPinningState = useTableColumnPinning(tanstackTableInstance);

  const tableCells = useMemo(() => {
    // Determine the id of the first real data column ahead of time (excluding
    // special columns like selector/detail expander) so we know where to place
    // the tree expand toggle, without mutating state inside map().
    const firstDataColumnId = columns
      .map(getColumnId)
      .find(colId => colId !== SELECTOR_ID && colId !== DETAIL_EXPANDER_ID);

    return columns.map(cell => {
      // Get the column from tanstackTableInstance for pinning styles
      const colId = getColumnId(cell);

      const isSelector = colId === SELECTOR_ID;

      const isDetailExpander = colId === DETAIL_EXPANDER_ID;

      if (isDetailExpander)
        return (
          <WbTableCell
            data-id={colId}
            colId={colId}
            className={classes.privateCell}
          >
            <WbExpandToggle
              expanded={detailExpanded}
              onClick={() => row.toggleExpanded()}
            />
          </WbTableCell>
        );

      if (isSelector && selection) {
        return (
          <WbTableCell colId={colId} className={classes.privateCell}>
            <Selector
              selection={selection}
              row={row.original}
              getRowId={getRowId!}
              selectedItems={selectedItems!}
              setSelectedItems={setSelectedItems!}
              disabled={checkIfDisabled?.(row.original)}
              tooltipIfDisabled={tooltipIfDisabled}
            />
          </WbTableCell>
        );
      }

      const isFirstDataColumn = colId === firstDataColumnId;

      let content = (
        <MemoizedTableCellContent<T>
          row={row.original}
          cell={cell}
          rowDepth={rowDepth}
        />
      );

      if (id && treeProps && isFirstDataColumn && getRowId && isAnyRowNested) {
        const expanded = treeProps.expandedRowsIds.includes(id);
        const hasChildren =
          treeProps.hasChildren?.(row.original) ??
          treeProps.getRowChildren(row.original).length > 0;

        content = (
          <MemoizedTableTreeCellWrapper
            hasChildren={hasChildren}
            expanded={expanded}
            onClick={handleExpandClick}
            depth={rowDepth}
          >
            {content}
          </MemoizedTableTreeCellWrapper>
        );
      }

      const column = tanstackTableInstance.getColumn(colId);

      if (!column) throw new Error('Column misconfiguration');

      const isPinned = column.getIsPinned();

      const cellStyle = {
        textAlign:
          getColumnId(cell) === ACTIONS_COLUMN_ID
            ? ('right' as const)
            : ('left' as const),
        ...(isPinned ? getCommonPinningStyles(column, theme, false) : {}),
      };

      return (
        <WbTableCell key={colId} style={cellStyle} colId={colId}>
          {content}
        </WbTableCell>
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    columns,
    row.original,
    rowDepth,
    id,
    treeProps,
    getRowId,
    selectedItems,
    setSelectedItems,
    selection,
    isAnyRowNested,
    handleExpandClick,
    theme,
    tanstackTableInstance,
    columnPinningState,
    detailExpanded,
  ]);

  // Check for special sentinel rows used by async tree loading
  const loadMoreParentId = treeProps?.getLoadMoreParentId?.(row.original);
  const loadingParentId = treeProps?.getLoadingParentId?.(row.original);

  if (loadMoreParentId !== undefined) {
    const rowDepthForSentinel = depthMap.get(id ?? '') ?? 1;

    const specialCols = columns.filter(cell => {
      const colId = getColumnId(cell);
      return colId === SELECTOR_ID || colId === DETAIL_EXPANDER_ID;
    });
    const dataColSpan = columns.length - specialCols.length;

    return (
      <TableRow
        className={clsx(classes.tableRow, rowClassName)}
        style={{ ...rowStyle }}
      >
        {specialCols.map((_cell, i) => (
          <TableCell key={i} className={classes.privateCell} />
        ))}
        <TableCell colSpan={dataColSpan} className={classes.sentinelCell}>
          <TableTreeCellWrapper
            depth={rowDepthForSentinel}
            hasChildren={false}
            expanded={false}
            onClick={() => {}}
          >
            <WbButton
              variant="text"
              label={
                <Typography component="span" variant="body2">
                  Load more
                </Typography>
              }
              onClick={() => treeProps?.onLoadMoreChildren?.(loadMoreParentId)}
            />
          </TableTreeCellWrapper>
        </TableCell>
      </TableRow>
    );
  }

  if (loadingParentId !== undefined) {
    const rowDepthForSentinel = depthMap.get(id ?? '') ?? 1;

    const specialCols = columns.filter(cell => {
      const colId = getColumnId(cell);
      return colId === SELECTOR_ID || colId === DETAIL_EXPANDER_ID;
    });
    const dataColSpan = columns.length - specialCols.length;

    return (
      <TableRow
        className={clsx(classes.tableRow, rowClassName)}
        style={{ ...rowStyle }}
      >
        {specialCols.map((_cell, i) => (
          <TableCell key={i} className={classes.privateCell} />
        ))}
        <TableCell colSpan={dataColSpan} className={classes.sentinelCell}>
          <TableTreeCellWrapper
            depth={rowDepthForSentinel}
            hasChildren={false}
            expanded={false}
            onClick={() => {}}
          >
            <Typography variant="body2" component="span">
              <CircularProgress size={14} aria-label="Loading children" />
            </Typography>
          </TableTreeCellWrapper>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      <TableRow
        className={clsx(
          rowDynamicClassName?.(row.original),
          classes.tableRow,
          rowClassName,
        )}
        style={{
          ...computedRowStyle,
        }}
        onClick={handleRowClick}
      >
        {tableCells}
      </TableRow>
      {renderRowDetail && (
        <TableRow
          className={
            detailExpanded ? classes.tableRow : classes.detailTableRowHidden
          }
        >
          <TableCell
            className={classes.detailTableCell}
            colSpan={row.getAllCells().length}
          >
            <Collapse in={detailExpanded} timeout="auto" unmountOnExit>
              {renderRowDetail(row.original)}
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// TODO find a way to avoid using isEqual here, as it can be expensive
const MemoizedTableRowComponent = memo(
  TableRowComponent,
) as typeof TableRowComponent;

export function TableContent<T>({
  rowStyle,
  rowClassName,
}: TableContentProps<T>) {
  const theme = useTheme();

  const {
    columns,
    selection,
    selectedItems,
    setSelectedItems,
    tooltipIfDisabled,
    onRowClick,
    rowDynamicClassName,
    treeProps,
    getRowId,
    disableRowHighlightOnHover,
    checkIfDisabled,
    tanstackTableInstance,
    RowsWrapper,
    renderRowDetail,
  } = useWbTableContext<T>();

  const {
    rows: displayedRows,
    isAnyRowNested,
    depthMap,
  } = useMemo(() => {
    const modelRows = tanstackTableInstance.getRowModel().rows;

    if (!treeProps?.getRowChildren || !getRowId) {
      return {
        rows: modelRows,
        isAnyRowNested: false,
        depthMap: new Map<string, number>(),
      };
    }

    return explodeTreeRows(
      modelRows,
      getRowId,
      treeProps.getRowChildren,
      treeProps.expandedRowsIds ?? [],
      treeProps.hasChildren,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    tanstackTableInstance.getRowModel().rows,
    getRowId,
    treeProps?.getRowChildren,
    treeProps?.expandedRowsIds,
    treeProps?.hasChildren,
  ]);

  return (
    <TableBody>
      {displayedRows.map((row, index: number) => (
        <RowsWrapper
          data={row.original}
          key={getRowId ? getRowId(row.original) : `table-row-${index}`}
        >
          <MemoizedTableRowComponent
            row={row}
            index={index}
            columns={columns}
            selection={selection}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            tooltipIfDisabled={tooltipIfDisabled}
            onRowClick={onRowClick}
            rowDynamicClassName={rowDynamicClassName}
            treeProps={treeProps}
            getRowId={getRowId}
            checkIfDisabled={checkIfDisabled}
            disableRowHighlightOnHover={disableRowHighlightOnHover}
            theme={theme}
            rowStyle={rowStyle}
            rowClassName={rowClassName}
            depthMap={depthMap}
            isAnyRowNested={isAnyRowNested}
            tanstackTableInstance={tanstackTableInstance}
            renderRowDetail={renderRowDetail}
          />
        </RowsWrapper>
      ))}
    </TableBody>
  );
}
